/**
 * MEDIS v2 implementation of `GetAccessList`.
 * Routing (which calls use v2 and where they go) lives in `src/lib/medis/v2/client.ts`.
 *
 * v2 `POST /v2/GetAccessList`:
 *   - auth: Azure EasyAuth `X-ZUMO-AUTH` token (handled by `medisV2Api`)
 *   - body: the inner `Response` of `getAccessListResponse` (PascalCase); list
 *     under `AccessListDetails`. `DocStatus` is an int enum. Read
 *     case-insensitively via the helpers in `@/lib/medis/v2`.
 */
import {
  medisV2Api,
  MEDIS_V2_ROUTES,
  v2Url,
  unwrapV2,
  prop,
  asArray,
  parseV2Date,
  debugV2Shape,
  type GetAccessListResponseV2,
  type AccessDetailV2,
  type DocIdentifierV2,
  type GetDocumentResponseV2,
} from '@/lib/medis/v2';
import type { DocumentDownload, MedicalResult, ResultDocument } from '../types/result.types';

/** Far-past date so `modifiedSince` returns the full history rather than a delta. */
const EPOCH = '1900-01-01T00:00:00.000Z';

/** v2 `DocStatus` int enum → string status (used only if the server sends an int). */
const DOC_STATUS_BY_INT: Record<number, string> = {
  0: 'Unknown',
  1: 'Available',
  2: 'Downloadable',
  3: 'DoesNotExists',
};

function mapDocumentV2(dto: DocIdentifierV2): ResultDocument | null {
  const docId = prop<string>(dto, 'DocID');
  if (!docId) return null;
  // `DocStatus` is a string ("Downloadable") on the live server, but the swagger
  // documents an int enum — accept either form.
  const rawStatus = prop(dto, 'DocStatus');
  const docStatus =
    typeof rawStatus === 'number'
      ? (DOC_STATUS_BY_INT[rawStatus] ?? 'Unknown')
      : ((rawStatus as string) || 'Unknown');
  return {
    medisId: prop<string>(dto, 'MedisID') ?? '',
    docId,
    docETag: prop<string>(dto, 'DocETag') ?? null,
    docStatus,
    canDownload: docStatus === 'Downloadable',
  };
}

function mapResultV2(dto: AccessDetailV2): MedicalResult | null {
  const consId = prop<string>(dto, 'ConsID');
  if (!consId) return null;

  const consType = prop<string>(dto, 'ConsType') ?? '';
  const docCountAvail = prop<number>(dto, 'DocCountAvail') ?? 0;
  // `PhysicianID` / `SpecialityID` / `LocationID` are short MEDIS ids (e.g.
  // "00000010-0067") — the SAME form the nomenclature resolver is keyed by, so
  // they're passed through verbatim. The `Location` field is usually null and
  // is NOT a display name, so it's only a last-resort fallback.
  const rawLocation = prop(dto, 'LocationID') ?? prop(dto, 'Location');
  const documents = asArray<DocIdentifierV2>(prop(dto, 'Documents'))
    .map(mapDocumentV2)
    .filter((d): d is ResultDocument => d !== null);

  return {
    consId,
    consType,
    consDesc: prop<string>(dto, 'ConsDesc') ?? '',
    typeDesc: prop<string>(dto, 'TypeDesc') ?? consType,
    sampleCode: prop<string>(dto, 'SampleCode') ?? null,
    date: parseV2Date(prop<string>(dto, 'Date')),
    dateClosed: parseV2Date(prop<string>(dto, 'DateClosed')),
    isFinal: prop<boolean>(dto, 'IsFinal') ?? false,
    docCount: prop<number>(dto, 'DocCount') ?? 0,
    docCountAvail,
    hasAvailableDocuments: docCountAvail > 0,
    documents,
    physicianId: prop<string>(dto, 'PhysicianID') ?? null,
    locationId: rawLocation != null ? String(rawLocation) : null,
    specialityId: prop<string>(dto, 'SpecialityID') ?? null,
  };
}

/** Lists a patient's results/consultations via the v2 API. Newest first. */
export async function getResultsV2(patientFhirId: string): Promise<MedicalResult[]> {
  const route = MEDIS_V2_ROUTES.getAccessList;
  if (!route) throw new Error('v2 getAccessList route is disabled');

  const { data } = await medisV2Api.post(await v2Url(route), {
    patient: patientFhirId,
    onlyWithDoc: false,
    modifiedSince: EPOCH,
  });
  const body = unwrapV2<GetAccessListResponseV2>(data);
  debugV2Shape('GetAccessList', body);
  if (import.meta.env.DEV) {
    const sample = asArray<AccessDetailV2>(prop(body, 'AccessListDetails'))[0];
    if (sample) console.debug('[v2:GetAccessList] sample item:', sample);
  }

  return asArray<AccessDetailV2>(prop(body, 'AccessListDetails'))
    .map(mapResultV2)
    .filter((r): r is MedicalResult => r !== null)
    .sort((a, b) => (b.date?.getTime() ?? 0) - (a.date?.getTime() ?? 0));
}

/**
 * v2 `POST /v2/GetDocument` — resolves a document's download info.
 *
 * Like v1, the metadata is in the body (`getDocumentResponse`, PascalCase) and
 * the actual download URL arrives in the `Location` response header (pointing at
 * `/v2/Download/{token}/{validator}`). The download itself is fetched via
 * `fetchDocumentBlobV2` (that endpoint is EasyAuth-protected too).
 *
 * NOTE: reading `Location` cross-origin requires the v2 host to send
 * `Access-Control-Expose-Headers: Location` — otherwise the browser hides it and
 * `downloadUrl` comes back null. v2 calls go direct (no proxy).
 */
export async function getDocumentV2(
  patientFhirId: string,
  consId: string,
  doc: { docId: string; medisId: string; docETag: string | null },
): Promise<DocumentDownload> {
  const route = MEDIS_V2_ROUTES.getDocument;
  if (!route) throw new Error('v2 getDocument route is disabled');

  // NOTE: `eTag` is intentionally NOT sent — a non-null ETag makes the server
  // short-circuit to a "not modified" path, which this web flow doesn't use.
  const url = await v2Url(route);
  const response = await medisV2Api.post(url, {
    patientId: patientFhirId,
    consID: consId,
    documentId: doc.docId,
    medisId: doc.medisId,
  });

  const body = unwrapV2<GetDocumentResponseV2>(response.data);
  debugV2Shape('GetDocument', body);

  const canDownload = prop<boolean>(body, 'CanDownload') === true;
  const location = (response.headers?.['location'] as string | undefined) ?? null;
  const rawStatus = prop(body, 'Status');

  return {
    canDownload,
    downloadUrl: canDownload && location ? new URL(location, url).toString() : null,
    documentName: prop<string>(body, 'DocumentName') ?? null,
    contentType: prop<string>(body, 'ContentType') ?? null,
    status: rawStatus != null ? String(rawStatus) : null,
  };
}

/**
 * Fetches the v2 document bytes from its (absolute) download URL.
 *
 * `/v2/Download/{token}/{validator}` is EasyAuth-protected, so it goes through
 * `medisV2Api` (injects the `X-ZUMO-AUTH` of the download URL's host — issued
 * on demand when `Location` points at a host the grant did not cover —, strips
 * `Authorization`, renews the token on a 401) rather than a raw `fetch` with a
 * Bearer token like v1.
 * Re-wrapped as `application/pdf` so the browser renders it inline.
 */
export async function fetchDocumentBlobV2(
  downloadUrl: string,
  contentType?: string | null,
): Promise<Blob> {
  const res = await medisV2Api.get<ArrayBuffer>(downloadUrl, { responseType: 'arraybuffer' });
  return new Blob([res.data], { type: contentType || 'application/pdf' });
}
